import React, { useState, useEffect } from 'react';
import { MapPin, TrendingUp, Phone, Navigation, Eye, Star, MessageSquare, Clock, CheckCircle, AlertCircle } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface GBPPerformance {
  id: string;
  location_id: string;
  target_site: string;
  date: string;
  views_search: number;
  views_maps: number;
  actions_website: number;
  actions_phone: number;
  actions_driving_directions: number;
  created_at: string;
}

interface GBPReview {
  id: string;
  location_id: string;
  review_id: string;
  target_site: string;
  rating: number;
  comment: string;
  reviewer_name: string;
  create_time: string;
  reply_text: string | null;
  reply_time: string | null;
  created_at: string;
}

interface GbpLocationMappingRow {
  location_id: string;
  location_name: string;
}

interface LocationStats {
  location_id: string;
  location_name: string;
  total_views: number;
  total_actions: number;
  avg_rating: number;
  total_reviews: number;
  pending_replies: number;
}

export default function GBPDashboardPage() {
  const [performanceData, setPerformanceData] = useState<GBPPerformance[]>([]);
  const [reviews, setReviews] = useState<GBPReview[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedSite, setSelectedSite] = useState<string>('all');
  const [timeRange, setTimeRange] = useState<number>(30);
  const [generating, setGenerating] = useState(false);
  const [genProgress, setGenProgress] = useState({ current: 0, total: 0, status: '' });
  const [clearing, setClearing] = useState(false);

  const targetSites = ['halesac', 'myccs', 'abrahamac'];

  // Location names mapping (fallback for demo data)
  const fallbackLocationNames: Record<string, string> = {
    'halesac_broward': 'Hales AC - Broward',
    'halesac_palm': 'Hales AC - Palm Beach',
    'myccs_hillsborough': 'My CCS - Hillsborough',
    'myccs_pinellas': 'My CCS - Pinellas',
    'abrahamac_broward': 'Abraham AC - Broward',
    'abrahamac_palm': 'Abraham AC - Palm Beach'
  };

  const [locationNameById, setLocationNameById] = useState<Record<string, string>>(fallbackLocationNames);

  // Demo locations used for seeding
  const locations = [
    { location_id: 'halesac_broward', target_site: 'halesac' },
    { location_id: 'halesac_palm', target_site: 'halesac' },
    { location_id: 'myccs_hillsborough', target_site: 'myccs' },
    { location_id: 'myccs_pinellas', target_site: 'myccs' },
    { location_id: 'abrahamac_broward', target_site: 'abrahamac' },
    { location_id: 'abrahamac_palm', target_site: 'abrahamac' }
  ] as const;

  useEffect(() => {
    void fetchLocationMapping();
  }, []);

  useEffect(() => {
    fetchData();
  }, [timeRange, selectedSite]);

  async function fetchLocationMapping() {
    try {
      const { data, error } = await supabase
        .from('gbp_location_mapping')
        .select('location_id, location_name')
        .eq('is_active', true);

      if (error) throw error;

      const next: Record<string, string> = { ...fallbackLocationNames };
      for (const row of (data || []) as GbpLocationMappingRow[]) {
        next[row.location_id] = row.location_name;
      }

      setLocationNameById(next);
    } catch (error) {
      console.error('Error fetching GBP location mapping:', error);
      // Non-fatal: fall back to demo mapping.
    }
  }

  async function fetchData() {
    setLoading(true);
    try {
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - timeRange);

      // Fetch performance data
      let perfQuery = supabase
        .from('gbp_performance')
        .select('*')
        .gte('date', startDate.toISOString().split('T')[0])
        .order('date', { ascending: false });

      if (selectedSite !== 'all') {
        perfQuery = perfQuery.eq('target_site', selectedSite);
      }

      const { data: perfData, error: perfError } = await perfQuery;
      if (perfError) throw perfError;
      setPerformanceData(perfData || []);

      // Fetch reviews
      let reviewQuery = supabase
        .from('gbp_reviews')
        .select('*')
        .order('create_time', { ascending: false })
        .limit(50);

      if (selectedSite !== 'all') {
        reviewQuery = reviewQuery.eq('target_site', selectedSite);
      }

      const { data: reviewData, error: reviewError } = await reviewQuery;
      if (reviewError) throw reviewError;
      setReviews(reviewData || []);
    } catch (error) {
      console.error('Error fetching GBP data:', error);
    } finally {
      setLoading(false);
    }
  }

  // Generate 30 days of demo GBP data for locations
  async function generateDemoGBPData() {
    setGenerating(true);
    setGenProgress({ current: 0, total: 0, status: 'Initializing...' });
    try {
      const days = 30;
      const activeLocations = locations.filter(
        (l) => selectedSite === 'all' || l.target_site === selectedSite
      );
      const total = activeLocations.length * days;
      setGenProgress({ current: 0, total, status: 'Generating data...' });

      const dates: string[] = Array.from({ length: days }, (_, i) => {
        const d = new Date();
        d.setDate(d.getDate() - (days - 1 - i));
        return d.toISOString().split('T')[0];
      });

      const clamp = (v: number, min: number, max: number) =>
        Math.max(min, Math.min(max, Math.round(v)));

      // Seed performance metrics
      let batch: any[] = [];
      let counter = 0;

      for (const loc of activeLocations) {
        // Site baselines
        const siteBase =
          loc.target_site === 'halesac' ? 420 :
          loc.target_site === 'myccs' ? 360 : 300;

        for (const date of dates) {
          const dayIdx = dates.indexOf(date);
          const weeklyWave = 1 + 0.08 * Math.sin((2 * Math.PI * dayIdx) / 7);
          const jitter = 0.85 + Math.random() * 0.3;

          const totalViews = siteBase * weeklyWave * jitter;

          const views_search = clamp(totalViews * (0.62 + Math.random() * 0.08), 60, 1200);
          const views_maps = clamp(totalViews * (0.30 + Math.random() * 0.08), 30, 900);

          const actions_website = clamp(views_search * (0.045 + Math.random() * 0.02), 2, 120);
          const actions_phone = clamp(views_maps * (0.035 + Math.random() * 0.02), 2, 100);
          const actions_driving_directions = clamp(views_maps * (0.028 + Math.random() * 0.02), 1, 90);

          batch.push({
            location_id: loc.location_id,
            target_site: loc.target_site,
            date,
            views_search,
            views_maps,
            actions_website,
            actions_phone,
            actions_driving_directions
          });

          counter++;
          if (batch.length >= 500) {
            const { error } = await supabase
              .from('gbp_performance')
              .upsert(batch, { onConflict: 'location_id,date' as any });
            if (error) console.error('Upsert gbp_performance error:', error);
            batch = [];
          }

          setGenProgress((p) => ({ ...p, current: counter }));
          if (counter % 150 === 0) await new Promise((r) => setTimeout(r, 0));
        }
      }

      if (batch.length > 0) {
        const { error } = await supabase
          .from('gbp_performance')
          .upsert(batch, { onConflict: 'location_id,date' as any });
        if (error) console.error('Upsert gbp_performance error (final):', error);
      }

      // Seed demo reviews
      const sampleComments = [
        'Excellent service and quick response!',
        'Technician was professional and friendly.',
        'Fixed our AC fast, highly recommend.',
        'Great experience from start to finish.',
        'Reasonable pricing and quality work.',
        'They went above and beyond!',
        'Scheduling was easy and convenient.'
      ];
      const sampleNames = ['Alex P.', 'Jordan R.', 'Taylor S.', 'Chris M.', 'Sam K.', 'Morgan L.', 'Riley D.'];

      const reviewRows: any[] = [];
      for (const loc of activeLocations) {
        const reviewCount = 2 + Math.floor(Math.random() * 3); // 2-4 reviews/location
        for (let i = 0; i < reviewCount; i++) {
          const dayOffset = Math.floor(Math.random() * days);
          const d = new Date();
          d.setDate(d.getDate() - dayOffset);

          const rating = Math.random() < 0.15 ? 4 : 5;
          const comment = sampleComments[Math.floor(Math.random() * sampleComments.length)];
          const reviewer_name = sampleNames[Math.floor(Math.random() * sampleNames.length)];

          const replied = Math.random() < 0.6;
          const replyAdded = replied ? new Date(d.getTime() + 1000 * 60 * (60 + Math.floor(Math.random() * 600))) : null;

          reviewRows.push({
            location_id: loc.location_id,
            review_id: `demo_${loc.location_id}_${i}_${d.getTime()}`,
            target_site: loc.target_site,
            rating,
            comment,
            reviewer_name,
            create_time: d.toISOString(),
            reply_text: replied ? 'Thank you for your review! We appreciate your business.' : null,
            reply_time: replied ? replyAdded?.toISOString() : null
          });
        }
      }

      if (reviewRows.length > 0) {
        const { error } = await supabase
          .from('gbp_reviews')
          .upsert(reviewRows, { onConflict: 'review_id' as any });
        if (error) console.error('Upsert gbp_reviews error:', error);
      }

      setGenProgress((p) => ({ ...p, status: 'Complete. Reloading data...' }));
      await fetchData();
      setGenProgress({ current: total, total, status: 'Completed successfully.' });
    } catch (e: any) {
      console.error('Error generating demo GBP data:', e);
      setGenProgress({ current: 0, total: 0, status: `Error: ${e?.message || 'Unknown error'}` });
    } finally {
      setTimeout(() => setGenerating(false), 1500);
    }
  }

  // Clear demo GBP data for current selection (by target_site)
  async function clearDemoGBPData() {
    setClearing(true);
    try {
      const sites = selectedSite === 'all' ? targetSites : [selectedSite];

      const { error: perfErr } = await supabase
        .from('gbp_performance')
        .delete()
        .in('target_site', sites);
      if (perfErr) console.error('Clear gbp_performance error:', perfErr);

      const { error: revErr } = await supabase
        .from('gbp_reviews')
        .delete()
        .in('target_site', sites);
      if (revErr) console.error('Clear gbp_reviews error:', revErr);

      await fetchData();
    } catch (e) {
      console.error('Error clearing GBP data:', e);
    } finally {
      setClearing(false);
    }
  }

  // Calculate aggregated stats by location
  const locationStats: LocationStats[] = React.useMemo(() => {
    const statsMap = new Map<string, LocationStats>();

    performanceData.forEach(perf => {
      if (!statsMap.has(perf.location_id)) {
        statsMap.set(perf.location_id, {
          location_id: perf.location_id,
          location_name: locationNameById[perf.location_id] || perf.location_id,
          total_views: 0,
          total_actions: 0,
          avg_rating: 0,
          total_reviews: 0,
          pending_replies: 0
        });
      }

      const stats = statsMap.get(perf.location_id)!;
      stats.total_views += (perf.views_search || 0) + (perf.views_maps || 0);
      stats.total_actions += (perf.actions_website || 0) + (perf.actions_phone || 0) + (perf.actions_driving_directions || 0);
    });

    // Add review stats
    reviews.forEach(review => {
      if (!statsMap.has(review.location_id)) {
        statsMap.set(review.location_id, {
          location_id: review.location_id,
          location_name: locationNameById[review.location_id] || review.location_id,
          total_views: 0,
          total_actions: 0,
          avg_rating: 0,
          total_reviews: 0,
          pending_replies: 0
        });
      }

      const stats = statsMap.get(review.location_id)!;
      stats.total_reviews += 1;
      stats.avg_rating = ((stats.avg_rating * (stats.total_reviews - 1)) + review.rating) / stats.total_reviews;
      if (!review.reply_text) {
        stats.pending_replies += 1;
      }
    });

    return Array.from(statsMap.values());
  }, [performanceData, reviews, locationNameById]);

  // Calculate overall totals
  const totalViews = performanceData.reduce((sum, p) => sum + (p.views_search || 0) + (p.views_maps || 0), 0);
  const totalActions = performanceData.reduce((sum, p) => sum + (p.actions_website || 0) + (p.actions_phone || 0) + (p.actions_driving_directions || 0), 0);
  const avgRating = reviews.length > 0 ? reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length : 0;
  const pendingReplies = reviews.filter(r => !r.reply_text).length;

  // Recent reviews (last 10)
  const recentReviews = reviews.slice(0, 10);

  function formatDate(dateString: string) {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }

  function getTimeSince(dateString: string) {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return `${Math.floor(diffDays / 30)} months ago`;
  }

  function getRatingColor(rating: number) {
    if (rating >= 4.5) return 'text-green-600';
    if (rating >= 4.0) return 'text-blue-600';
    if (rating >= 3.5) return 'text-yellow-600';
    return 'text-red-600';
  }

  if (loading) {
    return (
      <div className="p-8">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <MapPin className="text-blue-600" size={32} />
          <h1 className="text-3xl font-bold text-gray-900">Google Business Profile Performance</h1>
        </div>
        <p className="text-gray-600">
          Track profile views, customer actions, and reviews across all locations
        </p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Target Site</label>
            <select
              value={selectedSite}
              onChange={(e) => setSelectedSite(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Sites</option>
              {targetSites.map(site => (
                <option key={site} value={site}>{site}.com</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Time Range</label>
            <select
              value={timeRange}
              onChange={(e) => setTimeRange(Number(e.target.value))}
              className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value={7}>Last 7 Days</option>
              <option value={30}>Last 30 Days</option>
              <option value={90}>Last 90 Days</option>
            </select>
          </div>
        </div>
      </div>

      <div className="mb-6 -mt-6">
        <div className="flex flex-wrap items-center gap-3">
          <button
            onClick={generateDemoGBPData}
            disabled={generating}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg font-medium ${generating ? 'bg-gray-300 text-gray-600 cursor-not-allowed' : 'bg-blue-600 text-white hover:bg-blue-700'}`}
          >
            {generating ? 'Generating Demo GBP Data...' : 'Generate Demo GBP Data'}
          </button>

          <button
            onClick={clearDemoGBPData}
            disabled={clearing}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg font-medium ${clearing ? 'bg-gray-300 text-gray-600 cursor-not-allowed' : 'bg-gray-800 text-white hover:bg-gray-900'}`}
          >
            {clearing ? 'Clearing...' : 'Clear Demo Data'}
          </button>

          {generating && genProgress.total > 0 && (
            <div className="flex-1 min-w-[240px]">
              <div className="text-sm text-gray-700 mb-1">
                {genProgress.status} {genProgress.current} / {genProgress.total}
              </div>
              <div className="w-full bg-blue-200 rounded-full h-2 overflow-hidden">
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${Math.min(100, Math.round((genProgress.current / genProgress.total) * 100))}%` }}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-gray-600 text-sm font-medium">Total Views</span>
            <Eye className="text-blue-600" size={20} />
          </div>
          <div className="text-3xl font-bold text-gray-900">{totalViews.toLocaleString()}</div>
          <div className="text-sm text-gray-500 mt-1">Search + Maps</div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-gray-600 text-sm font-medium">Total Actions</span>
            <TrendingUp className="text-green-600" size={20} />
          </div>
          <div className="text-3xl font-bold text-gray-900">{totalActions.toLocaleString()}</div>
          <div className="text-sm text-gray-500 mt-1">Clicks + Calls + Directions</div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-gray-600 text-sm font-medium">Average Rating</span>
            <Star className="text-yellow-600" size={20} />
          </div>
          <div className={`text-3xl font-bold ${getRatingColor(avgRating)}`}>
            {avgRating > 0 ? avgRating.toFixed(1) : 'N/A'}
          </div>
          <div className="text-sm text-gray-500 mt-1">{reviews.length} reviews</div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-gray-600 text-sm font-medium">Pending Replies</span>
            <MessageSquare className="text-orange-600" size={20} />
          </div>
          <div className="text-3xl font-bold text-gray-900">{pendingReplies}</div>
          <div className="text-sm text-gray-500 mt-1">Need response</div>
        </div>
      </div>

      {/* Location Performance */}
      <div className="bg-white rounded-lg shadow-md mb-8">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Location Performance</h2>
        </div>
        <div className="p-6">
          {locationStats.length === 0 ? (
            <div className="text-center py-12">
              <AlertCircle className="mx-auto text-gray-400 mb-4" size={48} />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No Performance Data</h3>
              <p className="text-gray-600 mb-6">
                Use the in-app generator to seed 30 days of demo GBP metrics for all locations.
              </p>
              <button
                onClick={generateDemoGBPData}
                disabled={generating}
                className={`inline-flex items-center gap-2 px-6 py-3 rounded-lg font-medium ${generating ? 'bg-gray-300 text-gray-600 cursor-not-allowed' : 'bg-blue-600 text-white hover:bg-blue-700'}`}
              >
                {generating ? 'Generating...' : 'Generate Demo GBP Data'}
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {locationStats.map(location => (
                <div key={location.location_id} className="border border-gray-200 rounded-lg p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="font-semibold text-gray-900">{location.location_name}</h3>
                      <div className="flex items-center gap-1 mt-1">
                        <Star className="text-yellow-500" size={16} fill="currentColor" />
                        <span className={`font-semibold ${getRatingColor(location.avg_rating)}`}>
                          {location.avg_rating > 0 ? location.avg_rating.toFixed(1) : 'N/A'}
                        </span>
                        <span className="text-gray-500 text-sm">({location.total_reviews})</span>
                      </div>
                    </div>
                    {location.pending_replies > 0 && (
                      <span className="bg-orange-100 text-orange-800 text-xs font-semibold px-2 py-1 rounded-full">
                        {location.pending_replies} pending
                      </span>
                    )}
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-gray-600">
                        <Eye size={16} />
                        <span className="text-sm">Views</span>
                      </div>
                      <span className="font-semibold text-gray-900">{location.total_views.toLocaleString()}</span>
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-gray-600">
                        <TrendingUp size={16} />
                        <span className="text-sm">Actions</span>
                      </div>
                      <span className="font-semibold text-gray-900">{location.total_actions.toLocaleString()}</span>
                    </div>

                    <div className="pt-2 border-t border-gray-100">
                      <div className="text-xs text-gray-500">
                        Conversion Rate: {location.total_views > 0 ? ((location.total_actions / location.total_views) * 100).toFixed(1) : '0.0'}%
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Recent Reviews */}
      <div className="bg-white rounded-lg shadow-md">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Recent Reviews</h2>
        </div>
        <div className="p-6">
          {recentReviews.length === 0 ? (
            <div className="text-center py-12">
              <MessageSquare className="mx-auto text-gray-400 mb-4" size={48} />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No Reviews Yet</h3>
              <p className="text-gray-600">
                Reviews will appear here once data is collected
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {recentReviews.map(review => (
                <div key={review.id} className="border border-gray-200 rounded-lg p-6">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-semibold text-gray-900">{review.reviewer_name}</span>
                        <span className="text-gray-400">•</span>
                        <span className="text-sm text-gray-500">{getTimeSince(review.create_time)}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        {[...Array(5)].map((_, i) => (
                          <Star
                            key={i}
                            size={16}
                            className={i < review.rating ? 'text-yellow-500' : 'text-gray-300'}
                            fill={i < review.rating ? 'currentColor' : 'none'}
                          />
                        ))}
                      </div>
                    </div>
                    <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                      {locationNameById[review.location_id] || review.location_id}
                    </span>
                  </div>

                  <p className="text-gray-700 mb-4">{review.comment}</p>

                  {review.reply_text ? (
                    <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
                      <div className="flex items-center gap-2 mb-2">
                        <CheckCircle className="text-blue-600" size={16} />
                        <span className="text-sm font-semibold text-blue-900">Response from Business</span>
                        <span className="text-xs text-blue-600">
                          {review.reply_time && formatDate(review.reply_time)}
                        </span>
                      </div>
                      <p className="text-sm text-blue-900">{review.reply_text}</p>
                    </div>
                  ) : (
                    <div className="bg-orange-50 border-l-4 border-orange-500 p-4 rounded flex items-center gap-3">
                      <Clock className="text-orange-600" size={20} />
                      <div>
                        <div className="text-sm font-semibold text-orange-900">Pending Response</div>
                        <div className="text-xs text-orange-700">This review needs a reply</div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Info Footer */}
      <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
        <div className="flex items-start gap-3">
          <AlertCircle className="text-blue-600 mt-1" size={20} />
          <div>
            <h4 className="font-semibold text-blue-900 mb-2">About GBP Performance Tracking</h4>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• Track profile views from Google Search and Maps</li>
              <li>• Monitor customer actions: website clicks, phone calls, driving directions</li>
              <li>• Respond to reviews quickly to improve local reputation</li>
              <li>• Compare performance across multiple locations</li>
              <li>• Ideal response time: Within 24 hours of review posting</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
